import React, { useMemo } from 'react'
import styled from 'styled-components'
import { ink, line, font } from 'features/dashboard/tokens'

const HorizontalStackedBars = ({ rows = [], buckets = [], limit = 12, onRowClick, emptyText = 'Nothing to show yet.' }) => {
  const ranked = useMemo(() => {
    const withTotals = rows.map(r => ({
      row: r,
      total: buckets.reduce((s, b) => s + Number(r[b.key] ?? 0), 0),
    }))
    return withTotals.filter(x => x.total > 0).sort((a, b) => b.total - a.total).slice(0, limit)
  }, [rows, buckets, limit])

  const max = useMemo(() => Math.max(1, ...ranked.map(x => x.total)), [ranked])

  if (!ranked.length) return <Empty>{emptyText}</Empty>

  return (
    <Wrap>
      {ranked.map(({ row, total }) => {
        const clickable = !!onRowClick
        return (
          <Row
            key={row.bpId || row.userId || row.name}
            as={clickable ? 'button' : 'div'}
            type={clickable ? 'button' : undefined}
            $clickable={clickable}
            onClick={clickable ? () => onRowClick(row) : undefined}
            title={buckets
              .filter(b => Number(row[b.key] ?? 0) > 0)
              .map(b => `${b.label}: ${row[b.key]}`)
              .join(' · ')}
          >
            <Name>{row.name}</Name>
            <Track style={{ width: `${(total / max) * 100}%` }}>
              {buckets.map(b => {
                const v = Number(row[b.key] ?? 0)
                if (!v) return null
                return <Seg key={b.key} style={{ width: `${(v / total) * 100}%`, background: b.tint }} />
              })}
            </Track>
            <Total>{total.toLocaleString('en-GB')}</Total>
          </Row>
        )
      })}
      {rows.length > ranked.length && (
        <More>Showing the {ranked.length} busiest of {rows.length}. The table below has them all.</More>
      )}
    </Wrap>
  )
}

export default HorizontalStackedBars

const Wrap = styled.div`display: flex; flex-direction: column; gap: 5px; width: 100%;`
const Row = styled.div`
  display: grid; grid-template-columns: minmax(0, 150px) 1fr auto; align-items: center; gap: 10px;
  width: 100%; padding: 3px 4px; border: none; background: transparent; text-align: left;
  font-family: inherit; border-radius: 6px; min-height: 26px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover { background: ${({ $clickable }) => ($clickable ? '#f0fbfc' : 'transparent')}; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 1px; }
  @media (max-width: 768px) { grid-template-columns: minmax(0, 104px) 1fr auto; gap: 7px; }
`
const Name = styled.span`
  font-size: ${font.caption}; color: ${ink.soft}; text-align: right;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`
const Track = styled.span`display: flex; height: 14px; border-radius: 3px; overflow: hidden; min-width: 2px;`
const Seg = styled.span`height: 100%; min-width: 1px;`
const Total = styled.span`
  font-size: ${font.caption}; font-weight: 700; color: ${ink.base};
  font-variant-numeric: tabular-nums; min-width: 34px; text-align: right;
`
const More = styled.div`font-size: ${font.caption}; color: ${ink.mute}; margin-top: 6px; padding-left: 4px;`
const Empty = styled.div`font-size: ${font.small}; color: ${ink.mute}; padding: 14px 2px;`
